import React, { useState } from 'react';

interface InterviewCardProps {
  title: string;
  href: string;
  source: 'YouTube' | 'Spotify';
  description: string[];
  date: string;
  imageSrc: string;
  imageAlt: string;
  host?: string;
}

const InterviewCard: React.FC<InterviewCardProps> = ({
  title,
  href,
  source,
  description,
  date,
  imageSrc,
  imageAlt,
  host,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <article className="flex flex-col md:flex-row md:items-stretch overflow-hidden">
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-full md:w-[38%] relative">
        {!imageFailed ? (
          <a href={href} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-full object-contain block"
              onError={() => setImageFailed(true)}
            />
          </a>
        ) : (
          <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
            Image placeholder
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-3 md:p-4 flex flex-col justify-between overflow-hidden">
        <div className="flex flex-col justify-between h-full">
          <div className="overflow-hidden">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-start gap-2 hover:text-[#0B6054] transition-colors"
            >
              <h2 className="text-[#1f2d3d] font-bold text-[14px] md:text-[18px] leading-snug md:leading-[1.2]">
                {title}
              </h2>
              <img
                src="/images/Arrow.webp"
                alt=""
                className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-70 group-hover:opacity-100"
                aria-hidden
              />
            </a>

            <div className="flex items-center gap-2 mt-1 text-[12px] md:text-[13px] text-gray-500">
              {source === 'YouTube' ? (
                <>
                  <img src="/images/youtube.webp" alt="" className="w-4 h-4" aria-hidden />
                  <span>YouTube</span>
                </>
              ) : (
                <>
                  <img src="/images/spotify.webp" alt="" className="w-4 h-4" aria-hidden />
                  <span>Spotify</span>
                </>
              )}
            </div>

            <div className="mt-2 space-y-1.5 text-[#667085] text-[14px] md:text-[13px] leading-[20px] font-normal overflow-hidden">
              {description.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            
          </div>

          <p className="mt-2 text-[#101828] text-[13px] md:text-[14px] leading-[20px] font-medium">
            {date}
          </p>
        </div>
      </div>
    </article>
  );
};

const interviews = [
  {
    title: "Is It Time to Sell Mining Stocks? | Rob Bruggeman (The Wealthy Miner) Interview",
    href: "https://www.youtube.com/watch?v=M4iCcykAaY8",
    source: 'YouTube' as const,
    description: [
      "If you’ve been watching the volatility in the resource sector, you’ve probably asked yourself the same question: is it time to sell mining stocks? In this in-depth interview, Rob Bruggeman of The Wealthy Miner shares his expert perspective on where we are in the current mining cycle, what smart investors should be watching, and how to position a portfolio in uncertain times.",
      "Tune in to hear Rob’s outlook on the future of mining stocks, the warning signs investors shouldn’t ignore, and the strategies he’s using right now inside his own portfolio.",
    ],
    date: "03 Feb 2026",
    imageSrc: "/images/interview1.webp",
    imageAlt: "Is It Time to Sell Mining Stocks - Rob Bruggeman Interview",
  },
  {
    title: "$5,000 gold isn't a lifeline: The Wealthy Miner's Robert Bruggeman on why most resource stocks still don't work",
    href: "https://open.spotify.com/episode/3tbcYqaYJN0ZuQ2YSwasHh",
    source: 'Spotify' as const,
    description: [
      "For investors chasing the gold narrative, this conversation offers a critical reality check. Learn what actually drives sustainable performance in mining equities, how to spot red flags early, and why selectivity matters more than ever in today’s market.",
      "If you’re wondering whether $5,000 gold guarantees profits across the sector — or if you’re trying to separate opportunity from hype — this interview delivers the clarity you need.",
    ],
    date: "03 Feb 2026",
    imageSrc: "/images/interview2.webp",
    imageAlt: "Rob Bruggeman on mining super-cycle - Spotify",
  },
];

const InterviewsSection: React.FC = () => {
  return (
    <section className="bg-[#f5f5f5] py-12 md:py-16">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="space-y-10 md:space-y-12">
          {interviews.map((item, index) => (
            <InterviewCard key={index} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InterviewsSection;